import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { IoSave } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { applicationAnimVariants } from '../../constants/animationVariants';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../constants/settings';
import { useApplicationContext } from '../../contexts/ApplicationProvider';
import { useAuthContext } from '../../contexts/AuthProvider';
import useApplicationActions from '../../hooks/APIHooks/users/useApplicationActions';
import useRolesActions from '../../hooks/APIHooks/users/useRolesActions';
import { saveProfessionalInfoSchema } from '../../schemas';
import { ApplicationStatus, UpdateAppReqBody } from '../../types/applications';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import AcademiaFormSelect from '../CommonComps/Inputs/AcademiaFormSelect';
import AcademiaInputField from '../CommonComps/Inputs/AcademiaInputField';
import SelectExpertiseDiv from './SelectExpertiseDiv';
import SelectSpokenLanguagesDiv from './SelectSpokenLanguagesDiv';

const ApplicationProfessionalInfo = ({
  applicationDetails,
}: {
  applicationDetails: ApplicationStatus | null;
}) => {
  // integration of API hooks here
  const { getRoleListHandler } = useRolesActions('roleList');

  const { updateApplicationHandler } = useApplicationActions();

  // integration of context hooks here
  const { selectedLanguages, selectedServices } = useApplicationContext();
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // formatting role options here
  const roleOptions = useMemo(() => {
    if (!getRoleListHandler.data?.is_success) return [];

    return getRoleListHandler.data.data
      .filter((role) => role.role_name.toLowerCase().includes('associate'))
      .map((role) => ({
        id: role.id,
        name: role.role_name,
      }));
  }, [getRoleListHandler.data]);

  // function to save application details declared here
  const saveApplicationHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      if (!applicationDetails || !currentUser) return;

      const { user, registration_request } = applicationDetails || {};

      console.log('APPLICATION FORM VALUES: ', values);

      const body: UpdateAppReqBody = {
        user: {
          id: user.id,
          role: values.role.id,
          updated_by: currentUser.username,
        },
        user_profile: {
          years_of_experience: values.years_of_experience,
          per_hour_rate: values.per_hour_rate,
          license_number: values.license_number,
          business_reg_no: values.business_reg_no,
          hst_no: values.hst_number,
          languages_spoken: selectedLanguages.map((lang) => ({
            id: lang.id,
          })),
          updated_by: currentUser.username,
        },
        user_expertise: selectedServices.map((service) =>
          applicationDetails.user_expertise.some(
            (expertise) => expertise.service_id === service.id
          )
            ? {
                id: applicationDetails.user_expertise.find(
                  (expertise) => expertise.service_id === service.id
                )?.id,
                service_id: service.id,
                updated_by: currentUser.username,
              }
            : {
                service_id: service.id,
                created_by: currentUser.username,
              }
        ),
        registration_request: {
          id: registration_request.id,
          updated_by: user.username,
          is_profile_complete: true,
          is_resubmit:
            registration_request.status === 'resubmit' ? true : false,
          is_view: false,
          user: user.id,
        },
      };

      await updateApplicationHandler
        .mutateAsync({
          uuid: applicationDetails.user.uid,
          body,
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('Application Updated!!');

            queryClient.invalidateQueries({ queryKey: ['application-status'] });
          } else {
            toast.error(res.message || 'Application Submission Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering application professional info component here
  return (
    <motion.div
      variants={applicationAnimVariants}
      initial='initial'
      animate='animate'
      className='px-5 py-4 shadow-academia-sm rounded-xl'
    >
      <Formik
        initialValues={{
          role: applicationDetails?.user.role
            ? {
                id: applicationDetails.user.role,
                name: applicationDetails.user.role_name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          license_number:
            applicationDetails?.user_profile?.license_number || '',
          business_reg_no:
            applicationDetails?.user_profile?.business_reg_no || '',
          hst_number: applicationDetails?.user_profile?.hst_no || '',
          years_of_experience:
            applicationDetails?.user_profile?.years_of_experience || '',
          per_hour_rate: applicationDetails?.user_profile?.per_hour_rate || '',
        }}
        validationSchema={saveProfessionalInfoSchema}
        onSubmit={saveApplicationHandler}
      >
        {(props) => (
          <Form className='grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5'>
            <AcademiaFormSelect
              name='role'
              label={`Select Applicant's Role`}
              options={roleOptions}
              defaultValue={props.values.role}
              setValueOnChange={(role) => props.setFieldValue('role', role.id)}
              showSearchInput
              isDisabled={!roleOptions.length}
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='years_of_experience'
              label='Years of Experience'
              placeholder='Enter your experience in years here...'
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='per_hour_rate'
              label='Expected Hourly Rate'
              placeholder='Enter expected rate per hour in CAD here...'
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='license_number'
              label='License Number'
              placeholder='Enter your license number here...'
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='business_reg_no'
              label='Business Registration Number'
              placeholder='Enter your business registration number here...'
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='hst_number'
              label='HST/TIN Number'
              placeholder='Enter your HST/TIN number here...'
              isRequired
            />

            <SelectExpertiseDiv />

            <SelectSpokenLanguagesDiv />

            <div className='md:col-span-2 mt-6 flex items-center justify-end'>
              <AcademiaButton
                title={
                  props.values.role.id === -1
                    ? 'Please, select an application role first!'
                    : !selectedServices.length
                    ? 'Please, select some field of expertise first!'
                    : !selectedLanguages.length
                    ? 'Please, select some languages first!'
                    : undefined
                }
                type='submit'
                variant='premium'
                isLoading={props.isSubmitting}
                icon={<IoSave className='size-6' />}
                additionalClassNames='w-full md:w-fit'
                isDisabled={
                  props.values.role.id === -1 ||
                  !selectedServices.length ||
                  !selectedLanguages.length
                }
              >
                Save Application
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

export default ApplicationProfessionalInfo;
